import { Fragment } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { currentPageContext, WidgetApi } from './api';
import { DetailsSheet, MessageBlocks } from './Blocks';
import { renderText } from './format';
import { connectRealtime, type WidgetRealtime } from './realtime';
import { loadToken, saveToken } from './storage';
import { TeamCall, teamCallSupported, type TeamCallEnd, type TeamCallState } from './team-call';
import { VoiceClient, voiceSupported, type VoiceState } from './voice';
import { NoteBar, NotePlayer } from './NoteBar';
import { noteRecordingSupported, type RecordedNote } from './recorder';
import type { VoiceEndReason } from './types';
import type {
  ChatStreamEvent,
  ConversationStatus,
  AskDetailsConfig,
  MessageBlock,
  PreChatConfig,
  PreChatFields,
  VisitorInfo,
  WidgetConfig,
  WidgetConversationSummary,
  WidgetMessage,
  WidgetPublicConfig,
} from './types';

export interface AppProps {
  widgetKey: string;
  apiUrl: string;
  position?: 'left' | 'right';
  /** Unsaved settings layered over the saved ones (dashboard live preview). */
  override?: Partial<WidgetConfig>;
  /** Open the panel straight away (dashboard live preview). */
  startOpen?: boolean;
  /** Injected in tests. */
  api?: WidgetApi;
  connect?: typeof connectRealtime;
}

type Phase = 'loading' | 'ready' | 'unavailable';
type View = 'home' | 'chat';

const localId = () => `local-${Math.random().toString(36).slice(2)}`;
/** Matches the CSS close animation; the panel unmounts after it. */
const CLOSE_MS = 200;
const TEASER_DELAY_MS = 4000;

/** Dark or light text, whichever reads better on the brand colour. */
export function readableOn(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '#ffffff';
  const n = parseInt(m[1]!, 16);
  const luminance =
    (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return luminance > 0.62 ? '#111827' : '#ffffff';
}

/**
 * CSS variables for the brand colour: its RGB channels (for translucent tints) and a deeper
 * shade for gradients. Computed here rather than with CSS color-mix() so older browsers work.
 */
export function brandVars(hex: string): Record<string, string> {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  const n = m ? parseInt(m[1]!, 16) : 0x111827;
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  const deep = (c: number) => Math.round(c * 0.7);
  return {
    '--vs-primary': `rgb(${r} ${g} ${b})`,
    '--vs-primary-rgb': `${r} ${g} ${b}`,
    '--vs-primary-deep': `rgb(${deep(r)} ${deep(g)} ${deep(b)})`,
    '--vs-primary-fg': readableOn(hex),
  };
}

/** "just now", "5 min ago", "3 h ago", or a date. */
function ago(iso: string): string {
  const sec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} h ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** Ends after which calling straight back makes sense (not limits, a handoff or no voice). */
const canCallAgain = (reason: VoiceEndReason | null) =>
  reason === 'completed' || reason === 'idle' || reason === 'error' || reason === 'limit_call';

/** The newest AI answer in a chat (the one the rating buttons show under). */
function lastAnswerId(list: WidgetMessage[]): string | null {
  for (let i = list.length - 1; i >= 0; i--) if (list[i]!.senderType === 'ai') return list[i]!.id;
  return null;
}

/** The call screen's look (shared with the voice assistant) for each team-call state. */
const TEAM_CALL_LOOK: Record<TeamCallState, string> = {
  ringing: 'connecting',
  connecting: 'connecting',
  live: 'listening',
  ended: 'ended',
};
const TEAM_CALL_ENDED: Record<TeamCallEnd, string> = {
  missed: 'Sorry, nobody could pick up right now.',
  ended: 'Thanks for calling!',
  cancelled: 'Call cancelled.',
  failed: "The call couldn't connect on this network. Please try again.",
  mic: 'Allow the microphone in your browser to call us.',
  busy: "We couldn't place the call. Please try again.",
};

/** "m:ss" for the call timer. */
function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

const initial = (name: string | null | undefined) => (name?.trim()[0] ?? '?').toUpperCase();

function timeOf(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function teaserKey(widgetKey: string) {
  return `vixsync:${widgetKey}:teaser`;
}

function teaserDismissed(widgetKey: string): boolean {
  try {
    return sessionStorage.getItem(teaserKey(widgetKey)) === '1';
  } catch {
    return false;
  }
}

function dismissTeaser(widgetKey: string) {
  try {
    sessionStorage.setItem(teaserKey(widgetKey), '1');
  } catch {
    // Storage blocked: the teaser may show again on the next page, which is harmless.
  }
}

const Icon = {
  chat: (
    <path
      d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8a2.5 2.5 0 0 1-2.5 2.5H9l-5 4V5.5z"
      stroke="currentColor"
      stroke-width="2"
      stroke-linejoin="round"
    />
  ),
  down: (
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      stroke-width="2.2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  ),
  close: (
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
  ),
  back: (
    <path
      d="M15 5l-7 7 7 7"
      stroke="currentColor"
      stroke-width="2.2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  ),
  arrow: (
    <path
      d="M5 12h14M13 6l6 6-6 6"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  ),
  send: (
    <path
      d="M5 12l14-7-5 15-2.5-6L5 12z"
      stroke="currentColor"
      stroke-width="2"
      stroke-linejoin="round"
    />
  ),
  phone: (
    <path
      d="M5 4h3l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z"
      stroke="currentColor"
      stroke-width="2"
      stroke-linejoin="round"
    />
  ),
  /** A pen on a square: start a new chat. */
  compose: (
    <path
      d="M12 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6M17.5 3.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4 8.5-8.5z"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  ),
  /** "Skip forward": a play arrow with a bar. */
  skip: (
    <path
      d="M6 6l9 6-9 6V6zM18 6v12"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  ),
  thumbUp: (
    <path
      d="M7 11v9H4v-9h3zm0 0l4-8a2.5 2.5 0 0 1 2.5 2.5V9h5a2 2 0 0 1 2 2.3l-1.2 7A2 2 0 0 1 17.3 20H7"
      stroke="currentColor"
      stroke-width="1.8"
      stroke-linejoin="round"
    />
  ),
  thumbDown: (
    <path
      d="M17 13V4h3v9h-3zm0 0l-4 8a2.5 2.5 0 0 1-2.5-2.5V15h-5a2 2 0 0 1-2-2.3l1.2-7A2 2 0 0 1 6.7 4H17"
      stroke="currentColor"
      stroke-width="1.8"
      stroke-linejoin="round"
    />
  ),
  mic: (
    <path
      d="M12 3a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3zM5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  ),
  micOff: (
    <path
      d="M15 10V6a3 3 0 0 0-5.7-1.3M9 9v3a3 3 0 0 0 4.6 2.5M18.5 11a6.5 6.5 0 0 1-1.2 3.8M5.5 11a6.5 6.5 0 0 0 10.2 5.3M12 17.5V21M4 4l16 16"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  ),
  hangup: (
    <path
      d="M3.5 14.5c4.7-4.3 12.3-4.3 17 0l-2 2.5-3.3-1.3v-2.4a10 10 0 0 0-6.4 0v2.4L5.5 17z"
      fill="currentColor"
    />
  ),
  spark: (
    <path
      d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3zM18.5 15l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2z"
      fill="currentColor"
    />
  ),
};

function Svg({ children, size = 20 }: { children: preact.ComponentChildren; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {children}
    </svg>
  );
}

/** The assistant's avatar: a sparkle on the brand colour, with an online dot. */
function BotAvatar({
  size = 'md',
  online,
  src,
  bg = '#ffffff',
}: {
  size?: 'sm' | 'md' | 'lg';
  online?: boolean;
  /** The business's logo or avatar; the sparkle shows if it's missing or fails to load. */
  src?: string | null;
  /** Behind the logo (a hex colour or "transparent"). */
  bg?: string;
}) {
  const [broken, setBroken] = useState(false);
  return (
    <span
      class={`vs-avatar vs-avatar-${size} vs-avatar-bot${src && !broken ? ' has-img' : ''}`}
      style={src && !broken ? { background: bg } : undefined}
    >
      {src && !broken ? (
        <img src={src} alt="" referrerpolicy="no-referrer" onError={() => setBroken(true)} />
      ) : (
        <Svg size={size === 'sm' ? 14 : size === 'lg' ? 22 : 18}>{Icon.spark}</Svg>
      )}
      {online && <span class="vs-online" aria-hidden="true" />}
    </span>
  );
}

/**
 * The logo on the home screen: a rounded plate that grows sideways for wide logos (wordmarks),
 * so the whole logo shows. Falls back to the round avatar without a logo or if it fails.
 */
function LogoPlate({ src, bg }: { src: string; bg: string }) {
  const [broken, setBroken] = useState(false);
  if (broken) return <BotAvatar size="lg" online />;
  return (
    <span class="vs-logo-plate" style={{ background: bg }}>
      <img src={src} alt="" referrerpolicy="no-referrer" onError={() => setBroken(true)} />
      <span class="vs-online" aria-hidden="true" />
    </span>
  );
}

const leadKey = (widgetKey: string) => `vixsync:${widgetKey}:lead`;

function leadSaved(widgetKey: string): boolean {
  try {
    return localStorage.getItem(leadKey(widgetKey)) === '1';
  } catch {
    return false;
  }
}

function rememberLead(widgetKey: string) {
  try {
    localStorage.setItem(leadKey(widgetKey), '1');
  } catch {
    // Storage blocked: the form may show again on the next visit.
  }
}

/**
 * The chat rating is asked once per chat. A chat is known by its first real message, so a new
 * chat (or a restored one) is recognised without a round trip.
 */
const ratedKey = (widgetKey: string) => `vixsync:${widgetKey}:rated`;
const chatMarker = (list: WidgetMessage[]) =>
  list.find((m) => !m.id.startsWith('local-'))?.id ?? null;

function wasRated(widgetKey: string, list: WidgetMessage[]): boolean {
  const marker = chatMarker(list);
  try {
    return marker !== null && localStorage.getItem(ratedKey(widgetKey)) === marker;
  } catch {
    return false;
  }
}

function markRated(widgetKey: string, list: WidgetMessage[]) {
  const marker = chatMarker(list);
  try {
    if (marker) localStorage.setItem(ratedKey(widgetKey), marker);
  } catch {
    // Storage blocked: the rating may be asked again in this chat.
  }
}

/** How long a chat sits quiet after an answer before the rating is offered. */
const RATE_AFTER_IDLE_MS = 2 * 60_000;

type AskStep = 'name' | 'phone' | 'email';
type AskState = 'idle' | 'done' | 'dismissed';
const ASK_ORDER: AskStep[] = ['name', 'phone', 'email'];
const askKey = (widgetKey: string) => `vixsync:${widgetKey}:ask`;

function readAskState(widgetKey: string): AskState {
  try {
    const v = localStorage.getItem(askKey(widgetKey));
    return v === 'done' || v === 'dismissed' ? v : 'idle';
  } catch {
    return 'idle';
  }
}

function saveAskState(widgetKey: string, state: AskState) {
  try {
    localStorage.setItem(askKey(widgetKey), state);
  } catch {
    // Storage blocked: the assistant may ask again on the next visit.
  }
}

const firstNameOf = (name: string | null | undefined) => name?.trim().split(/\s+/)[0] || null;

/** The next detail to ask for: in order, skipping what's switched off or already known. */
function nextAskStep(
  ask: AskDetailsConfig,
  visitor: VisitorInfo,
  after: AskStep | null,
): AskStep | null {
  const start = after ? ASK_ORDER.indexOf(after) + 1 : 0;
  for (const step of ASK_ORDER.slice(start)) {
    if (ask[step] === 'off') continue;
    if (step === 'name' && visitor.name) continue;
    if (step === 'phone' && visitor.hasPhone) continue;
    if (step === 'email' && visitor.hasEmail) continue;
    return step;
  }
  return null;
}

/** What the assistant says for each step, using the visitor's name once it knows it. */
function askText(step: AskStep | 'end', first: string | null): string {
  switch (step) {
    case 'name':
      return 'By the way, what should I call you? 😊';
    case 'phone':
      return first
        ? `Nice to meet you, ${first}! What's the best number for our team to reach you on?`
        : "What's the best number for our team to reach you on?";
    case 'email':
      return first
        ? `Thanks, ${first}! And your email, so we can send you the details?`
        : 'And your email, so we can send you the details?';
    case 'end':
      return first
        ? `Thank you, ${first}! 🙌 Our team will be in touch soon. Anything else I can help with?`
        : 'Thank you! 🙌 Our team will be in touch soon. Anything else I can help with?';
  }
}

const ASK_INPUT = {
  name: { type: 'text', inputMode: 'text', autocomplete: 'name', placeholder: 'Your name' },
  phone: { type: 'tel', inputMode: 'tel', autocomplete: 'tel', placeholder: 'Phone number' },
  email: {
    type: 'email',
    inputMode: 'email',
    autocomplete: 'email',
    placeholder: 'you@example.com',
  },
} as const;

/** A quick check before sending; the API validates properly. */
function askError(step: AskStep, value: string): string | null {
  const v = value.trim();
  if (!v) return 'Please type your answer, or tap Skip.';
  if (step === 'phone' && (v.replace(/\D/g, '').length < 7 || /[^\d\s+()-]/.test(v))) {
    return 'That doesn’t look like a phone number.';
  }
  if (step === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
    return 'That doesn’t look like an email address.';
  }
  return null;
}

/** "Hi! How can I help?" → "Hi Vipul! 👋 How can I help?" for a visitor it knows. */
function personalGreeting(greeting: string, first: string | null): string {
  if (!first) return greeting;
  return `Hi ${first}! 👋 ${greeting.replace(/^(hi|hello|hey|namaste)\b[!,.]*\s*/i, '')}`.trim();
}

/** The page the visitor is on (kept with a consent, as proof of where it was given). */
const pageUrl = () => {
  try {
    return window.location.href.slice(0, 1000);
  } catch {
    return '';
  }
};

const FIELD_LABELS = { name: 'Your name', email: 'Email', phone: 'Phone' } as const;
const FIELD_TYPES = { name: 'text', email: 'email', phone: 'tel' } as const;
const FIELD_AUTOCOMPLETE = { name: 'name', email: 'email', phone: 'tel' } as const;

/** The pre-chat form: the fields the business asks for, before the first message. */
function PreChatForm({
  form,
  ready,
  onSubmit,
  optInLabel = null,
  callOptInLabel = null,
}: {
  form: PreChatConfig;
  ready: boolean;
  onSubmit(fields: PreChatFields): Promise<Record<string, string> | null>;
  /** The WhatsApp opt-in checkbox's text (shown with the phone field), or null. */
  optInLabel?: string | null;
  /** The calls consent checkbox's text (shown with the phone field), or null. */
  callOptInLabel?: string | null;
}) {
  const fields = (['name', 'email', 'phone'] as const).filter((k) => form[k] !== 'off');
  const [values, setValues] = useState<PreChatFields>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    const missing: Record<string, string> = {};
    for (const k of fields) {
      if (form[k] === 'required' && !values[k]?.trim()) missing[k] = 'Required';
    }
    setErrors(missing);
    if (Object.keys(missing).length) return;
    setPending(true);
    const result = await onSubmit(values);
    setPending(false);
    if (result) setErrors(result);
  }

  return (
    <form class="vs-prechat" onSubmit={submit} noValidate>
      <div class="vs-prechat-title">{form.title}</div>
      {fields.map((k, i) => (
        <div key={k} class="vs-field" style={{ animationDelay: `${i * 60}ms` }}>
          <label for={`vs-pc-${k}`}>
            {FIELD_LABELS[k]}
            {form[k] === 'optional' && <span class="vs-optional"> (optional)</span>}
          </label>
          <input
            id={`vs-pc-${k}`}
            type={FIELD_TYPES[k]}
            autocomplete={FIELD_AUTOCOMPLETE[k]}
            value={values[k] ?? ''}
            aria-invalid={errors[k] ? true : undefined}
            aria-describedby={errors[k] ? `vs-pc-${k}-err` : undefined}
            onInput={(e) => setValues((v) => ({ ...v, [k]: (e.target as HTMLInputElement).value }))}
          />
          {errors[k] && (
            <div id={`vs-pc-${k}-err`} class="vs-field-error">
              {errors[k]}
            </div>
          )}
        </div>
      ))}
      {optInLabel && fields.includes('phone') && (
        <label class="vs-optin">
          <input
            type="checkbox"
            checked={values.whatsappOptIn ?? false}
            onChange={(e) =>
              setValues((v) => ({ ...v, whatsappOptIn: (e.target as HTMLInputElement).checked }))
            }
          />
          <span>{optInLabel}</span>
        </label>
      )}
      {callOptInLabel && fields.includes('phone') && (
        <label class="vs-optin">
          <input
            type="checkbox"
            checked={values.callOptIn ?? false}
            onChange={(e) =>
              setValues((v) => ({ ...v, callOptIn: (e.target as HTMLInputElement).checked }))
            }
          />
          <span>{callOptInLabel}</span>
        </label>
      )}
      <button type="submit" class="vs-prechat-submit" disabled={!ready || pending}>
        {pending ? 'Starting…' : 'Start chat'}
      </button>
      <p class="vs-prechat-note">We use these details only to reply to you.</p>
    </form>
  );
}

export function App({
  widgetKey,
  apiUrl,
  position,
  override,
  startOpen = false,
  api: injected,
  connect = connectRealtime,
}: AppProps) {
  const api = useRef(injected ?? new WidgetApi(apiUrl, widgetKey)).current;
  const [phase, setPhase] = useState<Phase>('loading');
  const [loaded, setLoaded] = useState<WidgetPublicConfig | null>(null);
  const config =
    loaded && override ? { ...loaded, widget: { ...loaded.widget, ...override } } : loaded;
  const [open, setOpen] = useState(startOpen);
  const [mounted, setMounted] = useState(startOpen);
  const [view, setView] = useState<View>('home');
  const [token, setToken] = useState<string | null>(null);
  const [messages, setMessages] = useState<WidgetMessage[]>([]);
  const [streaming, setStreaming] = useState<string | null>(null);
  const [status, setStatus] = useState<ConversationStatus | null>(null);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [agentName, setAgentName] = useState<string | null>(null);
  const [typingName, setTypingName] = useState<string | null>(null);
  /** The chat rating card: stars, then (for 1–3 stars) an optional comment, then thanks. */
  const [csat, setCsat] = useState<'hidden' | 'asking' | 'comment' | 'thanks'>('hidden');
  const [csatRating, setCsatRating] = useState(0);
  const [csatComment, setCsatComment] = useState('');
  /** What to do once the visitor has rated (or skipped), e.g. start the new chat they asked for. */
  const csatThen = useRef<(() => void) | null>(null);
  const [unread, setUnread] = useState(0);
  const [teaser, setTeaser] = useState(false);
  const [leadDone, setLeadDone] = useState(() => leadSaved(widgetKey));
  const [visitor, setVisitor] = useState<VisitorInfo>({
    name: null,
    hasPhone: false,
    hasEmail: false,
  });
  /** The voice note bar is open in place of the message box. */
  const [noting, setNoting] = useState(false);
  const canNote = useRef(noteRecordingSupported()).current;
  /** The answer the knowledge base couldn't give: "Talk to our team" / "Call back" chips. */
  const [helpFor, setHelpFor] = useState<string | null>(null);
  /** Likely next questions under the latest answer. */
  const [followUps, setFollowUps] = useState<{ forId: string; items: string[] } | null>(null);
  const [askState, setAskState] = useState<AskState>(() => readAskState(widgetKey));
  const [askStep, setAskStep] = useState<AskStep | null>(null);
  const [askValue, setAskValue] = useState('');
  const [askErr, setAskErr] = useState<string | null>(null);
  const [askBusy, setAskBusy] = useState(false);
  /** The assistant's questions and the visitor's answers while asking for details (shown only here). */
  const [askLog, setAskLog] = useState<{ afterId: string | null; message: WidgetMessage }[]>([]);
  const askInputRef = useRef<HTMLInputElement>(null);
  /** After tapping a suggestion instead of answering, ask again only a couple of questions later. */
  const askDeferUntil = useRef(0);
  /** A details check with the server is in flight. */
  const askChecking = useRef(false);
  /** "details": asking for name/phone/email; "call": taking a callback request. */
  const [askMode, setAskMode] = useState<'details' | 'call'>('details');
  const callPhone = useRef('');
  /** The next message starts a new conversation ("New chat"). */
  const freshChat = useRef(false);
  /** The visitor's chats for "Recent chats" on the home screen. */
  const [recent, setRecent] = useState<WidgetConversationSummary[]>([]);
  /** An earlier (ended) chat being read; the current chat stays in `messages`. */
  const [oldChat, setOldChat] = useState<{ id: string; messages: WidgetMessage[] } | null>(null);
  /** A voice call with the assistant (shown over the chat), and the client running it. */
  const [voiceCall, setVoiceCall] = useState<{
    state: VoiceState;
    user: string;
    ai: string;
    maxSeconds: number;
    startedAt: number;
    muted: boolean;
    endedMessage: string | null;
    endedReason: VoiceEndReason | null;
  } | null>(null);
  const voiceRef = useRef<VoiceClient | null>(null);
  /** A voice call with the team, from the browser (shown over the chat). */
  const [teamCall, setTeamCall] = useState<{
    state: TeamCallState;
    agentName: string | null;
    startedAt: number;
    muted: boolean;
    endedReason: TeamCallEnd | null;
    endedMessage: string | null;
  } | null>(null);
  const teamCallRef = useRef<TeamCall | null>(null);
  const orbRef = useRef<HTMLDivElement>(null);
  const [, setTick] = useState(0);
  const realtime = useRef<WidgetRealtime | null>(null);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const openRef = useRef(open);
  openRef.current = open;
  /** The current chat's messages, for callbacks registered once (realtime, timers). */
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const statusRef = useRef(status);
  statusRef.current = status;
  /** A question picked on the home screen, sent once the session is ready. */
  const queued = useRef<string | null>(null);

  useEffect(() => () => voiceRef.current?.stop(), []);
  // Leaving the page hangs up, so the agent isn't left on a silent line.
  useEffect(() => {
    const hangUp = () => teamCallRef.current?.hangUp();
    window.addEventListener('pagehide', hangUp);
    return () => {
      window.removeEventListener('pagehide', hangUp);
      hangUp();
    };
  }, []);

  /** The details form over the chat (opened by the assistant, or by tapping its button). */
  const [detailsFor, setDetailsFor] = useState<{
    id: string;
    block: Extract<MessageBlock, { type: 'details_form' }>;
  } | null>(null);
  const detailsShown = useRef(new Set<string>());
  // A new form from the assistant opens by itself, once.
  useEffect(() => {
    const last = messages[messages.length - 1];
    const form = last?.blocks?.find((b) => b.type === 'details_form');
    if (!last || !form || form.type !== 'details_form' || detailsShown.current.has(last.id)) return;
    detailsShown.current.add(last.id);
    setDetailsFor({ id: last.id, block: form });
  }, [messages]);

  /** Typed details go to the assistant: over the call if one is live, else as a message. */
  async function submitDetails(
    details: { name: string; phone: string; email: string },
    whatsappOptIn = false,
    callOptIn = false,
  ) {
    setDetailsFor(null);
    setVisitor((v) => ({ ...v, name: details.name, hasPhone: true, hasEmail: true }));
    // Saved (and confirmed) first: the assistant only books once the form was submitted.
    if (token) {
      try {
        await api.lead(token, {
          source: 'details_form',
          ...details,
          ...(whatsappOptIn ? { whatsappOptIn: true } : {}),
          ...(callOptIn ? { callOptIn: true, pageUrl: pageUrl() } : {}),
        });
      } catch {
        // Never in the way of the form: the assistant can ask again if it didn't save.
      }
    }
    if (voiceRef.current?.sendDetails(details)) return;
    void send(
      `My details — Name: ${details.name} · Phone: ${details.phone} · Email: ${details.email}`,
    );
  }
  const callLive = voiceCall !== null && voiceCall.state !== 'ended';
  const teamLive = teamCall?.state === 'live';
  useEffect(() => {
    if (!callLive && !teamLive) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [callLive, teamLive]);

  // Config loads immediately so the launcher has the right colour and position.
  useEffect(() => {
    api
      .config()
      .then((c) => {
        setLoaded(c);
        setPhase('ready');
      })
      .catch(() => setPhase('unavailable'));
  }, [api]);

  // Keep the panel mounted while it animates closed.
  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    const t = setTimeout(() => setMounted(false), CLOSE_MS);
    return () => clearTimeout(t);
  }, [open]);

  // A friendly nudge next to the launcher, once per browser session.
  useEffect(() => {
    if (
      phase !== 'ready' ||
      open ||
      !config?.widget.greeting ||
      config.widget.showTeaser === false ||
      teaserDismissed(widgetKey)
    ) {
      return;
    }
    const t = setTimeout(() => setTeaser(true), TEASER_DELAY_MS);
    return () => clearTimeout(t);
  }, [phase, open, config, widgetKey]);

  // The session and history load on first open, keeping page loads light.
  useEffect(() => {
    if (!open || token || phase !== 'ready') return;
    let cancelled = false;
    (async () => {
      try {
        const session = await api.session(loadToken(widgetKey));
        saveToken(widgetKey, session.visitorToken);
        const history = await api.history(session.visitorToken);
        if (cancelled) return;
        setToken(session.visitorToken);
        setMessages(history.messages);
        setStatus(history.status);
        if (history.visitor) setVisitor(history.visitor);
        // A live conversation with the team opens straight into the chat.
        if (history.status === 'pending_human' || history.status === 'human_active') {
          setView('chat');
        }
      } catch {
        if (!cancelled) setNotice('Chat is unavailable right now. Please try again later.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, token, phase, api, widgetKey]);

  // Live updates from the team, once there is a session.
  useEffect(() => {
    if (!token) return;
    const addOnce = (message: WidgetMessage) =>
      setMessages((m) => (m.some((x) => x.id === message.id) ? m : [...m, message]));
    realtime.current = connect(apiUrl, token, {
      onMessage(message) {
        addOnce(message);
        if (message.senderType === 'agent') setTypingName(null);
        if (!openRef.current && message.senderType !== 'contact') setUnread((n) => n + 1);
      },
      onStatus(next, name) {
        const was = statusRef.current;
        setStatus(next);
        setAgentName(name);
        // The team closed the chat: ask once (a rating itself re-sends the status).
        if (next === 'closed' && was !== 'closed') askRating();
      },
      onTyping(name, typing) {
        setTypingName(typing ? (name ?? 'Our team') : null);
      },
      onCall(event) {
        const current = teamCallRef.current;
        if (!current) return;
        if (event.type === 'accepted') void current.accepted(event.callId, event.agentName);
        else if (event.type === 'signal') void current.signal(event.callId, event.signal);
        else current.ended(event.callId, event.reason);
      },
    });
    return () => {
      realtime.current?.close();
      realtime.current = null;
    };
  }, [token, apiUrl, connect]);

  // Send a question picked on the home screen as soon as the session exists.
  useEffect(() => {
    if (token && queued.current && !formPending()) {
      const q = queued.current;
      queued.current = null;
      void send(q);
    }
  }, [token, leadDone]);

  function onDraft(value: string, el?: HTMLTextAreaElement) {
    setDraft(value);
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }
    realtime.current?.typing(value.trim().length > 0);
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => realtime.current?.typing(false), 2500);
  }

  /** 👍/👎 on an answer; shown at once, saved in the background. */
  function rateAnswer(id: string, rating: 'up' | 'down') {
    if (!token) return;
    const update = (list: WidgetMessage[]) =>
      list.map((m) => (m.id === id ? { ...m, feedback: rating } : m));
    setMessages(update);
    setOldChat((c) => c && { ...c, messages: update(c.messages) });
    void api.feedback(token, id, rating).catch(() => undefined);
  }

  /** Rated or skipped: remember it for this chat, then carry on (e.g. to the new chat). */
  function finishRating(next: 'thanks' | 'hidden') {
    markRated(widgetKey, messagesRef.current);
    setCsat(next);
    const then = csatThen.current;
    csatThen.current = null;
    then?.();
  }

  async function rate(rating: number) {
    if (!token) return;
    setCsatRating(rating);
    try {
      await api.rate(token, rating);
      // A low rating asks what went wrong; the rating is already saved either way.
      if (rating <= 3) setCsat('comment');
      else finishRating('thanks');
    } catch {
      finishRating('hidden');
    }
  }

  async function sendRatingComment(skip: boolean) {
    const comment = csatComment.trim();
    if (!skip && comment && token) {
      await api.rate(token, csatRating, comment).catch(() => undefined);
    }
    setCsatComment('');
    finishRating('thanks');
  }

  /** Asks for a rating once per chat, and only after someone has answered. */
  function askRating(then?: () => void): boolean {
    const list = messagesRef.current;
    const answered = list.some(
      (m) => (m.senderType === 'ai' || m.senderType === 'agent') && !m.id.startsWith('local-'),
    );
    if (!answered || wasRated(widgetKey, list)) return false;
    csatThen.current = then ?? null;
    setCsat('asking');
    return true;
  }

  useEffect(() => {
    if (open && view === 'chat') inputRef.current?.focus();
  }, [open, token, view]);

  // A chat that has gone quiet after an answer offers the rating (once).
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (
      !open ||
      view !== 'chat' ||
      oldChat ||
      csat !== 'hidden' ||
      sending ||
      streaming !== null ||
      !last ||
      (last.senderType !== 'ai' && last.senderType !== 'agent')
    ) {
      return;
    }
    const timer = setTimeout(() => askRating(), RATE_AFTER_IDLE_MS);
    return () => clearTimeout(timer);
  }, [messages, open, view, oldChat, csat, sending, streaming]);

  const lastCount = useRef(0);
  useEffect(() => {
    const log = logRef.current;
    if (!log) return;
    const grew = messages.length !== lastCount.current;
    lastCount.current = messages.length;
    const nearBottom = log.scrollHeight - log.scrollTop - log.clientHeight < 160;
    // A new message always scrolls; streaming text only follows a reader at the bottom.
    if (!grew && !nearBottom) return;
    const behavior = streaming !== null && !grew ? 'auto' : 'smooth';
    if (log.scrollTo) log.scrollTo({ top: log.scrollHeight, behavior });
    else log.scrollTop = log.scrollHeight;
  }, [messages, streaming, open, view, typingName, sending, askLog, followUps, helpFor]);

  // Tells the business once per page load that this visitor opened the chat (team alerts).
  const openedSent = useRef(false);
  useEffect(() => {
    if (!open || !token || openedSent.current || override) return;
    openedSent.current = true;
    const { url, title } = currentPageContext();
    void Promise.resolve()
      .then(() => api.opened(token, { url, title }))
      .catch(() => undefined);
  }, [open, token, api, override]);

  function toggle() {
    if (open) voiceRef.current?.stop();
    setTeaser(false);
    dismissTeaser(widgetKey);
    setUnread(0);
    setOpen((o) => !o);
  }

  /** The business asks for details first, and this visitor hasn't given them yet. */
  function formPending(): boolean {
    const form = config?.widget.preChat;
    const asks = form && (['name', 'email', 'phone'] as const).some((k) => form[k] !== 'off');
    return Boolean(form?.enabled && asks && !leadDone && messages.length === 0);
  }

  /** Consent to calls (TRAI: needed before the business calls), with the phone number. */
  const callOptInLabel =
    config?.widget.call?.consentOptIn !== false && config?.widget.call?.consentLabel
      ? config.widget.call.consentLabel
      : null;

  /** WhatsApp is connected: the opt-in checkbox and the "Continue on WhatsApp" card. */
  const whatsAppOptInLabel =
    config?.whatsappNumber && config.widget.whatsapp?.optIn
      ? config.widget.whatsapp.optInLabel
      : null;
  const whatsAppButton =
    config?.whatsappNumber && config.widget.whatsapp?.button
      ? config.widget.whatsapp.buttonText
      : null;

  /** Opens WhatsApp with a message that links it to this chat. */
  async function openWhatsApp() {
    if (!token) return;
    // Opened first, filled after: browsers block pop-ups opened after an await.
    const tab = window.open('', '_blank');
    try {
      const url = await api.whatsAppLink(token);
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (err) {
      tab?.close();
      setNotice(err instanceof Error ? err.message : 'WhatsApp is not available right now');
    }
  }

  async function submitLead(fields: PreChatFields): Promise<Record<string, string> | null> {
    if (!token) return null;
    try {
      await api.lead(token, fields.callOptIn ? { ...fields, pageUrl: pageUrl() } : fields);
      rememberLead(widgetKey);
      setLeadDone(true);
      return null;
    } catch (err) {
      const fieldErrors = (err as { fieldErrors?: Record<string, string> }).fieldErrors ?? {};
      if (Object.keys(fieldErrors).length) return fieldErrors;
      setNotice(err instanceof Error ? err.message : 'Details could not be saved');
      return {};
    }
  }

  function startChat(question?: string) {
    setView('chat');
    if (question) {
      if (token && !formPending()) void send(question);
      else queued.current = question;
    }
  }

  async function send(text: string, opts: { handoff?: boolean } = {}) {
    const trimmed = text.trim();
    if (!trimmed || !token || sending) return;
    setHelpFor(null);
    setDraft('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setNotice(null);
    setSending(true);
    setCsat('hidden');
    setFollowUps(null);
    realtime.current?.typing(false);
    const pending: WidgetMessage = {
      id: localId(),
      senderType: 'contact',
      senderName: null,
      body: trimmed,
      citations: [],
      createdAt: new Date().toISOString(),
    };
    setMessages((m) => [...m, pending]);
    const { onEvent } = replyHandler(pending.id);
    try {
      const fresh = freshChat.current;
      freshChat.current = false;
      const sendOpts = {
        ...(fresh ? { newConversation: true } : {}),
        ...(opts.handoff ? { handoff: true } : {}),
      };
      if (Object.keys(sendOpts).length) {
        await api.send(token, trimmed, currentPageContext(), onEvent, sendOpts);
      } else {
        await api.send(token, trimmed, currentPageContext(), onEvent);
      }
    } catch (err) {
      setMessages((m) => m.filter((x) => x.id !== pending.id));
      setDraft(trimmed);
      setNotice(err instanceof Error ? err.message : 'Message could not be sent');
    } finally {
      setStreaming(null);
      setSending(false);
    }
  }

  /** Sends a recorded voice note; it's transcribed and answered like a typed message. */
  async function sendNote(note: RecordedNote, previewUrl: string) {
    setNoting(false);
    if (!token || sending) return;
    setHelpFor(null);
    setNotice(null);
    setSending(true);
    setCsat('hidden');
    setFollowUps(null);
    const pending: WidgetMessage = {
      id: localId(),
      senderType: 'contact',
      senderName: null,
      body: '',
      citations: [],
      voiceNote: { durationMs: note.durationMs },
      localAudioUrl: previewUrl,
      createdAt: new Date().toISOString(),
    };
    setMessages((m) => [...m, pending]);
    const handler = replyHandler(pending.id, previewUrl);
    const drop = () => setMessages((m) => m.filter((x) => x.id !== pending.id));
    try {
      const fresh = freshChat.current;
      freshChat.current = false;
      await api.sendVoiceNote(
        token,
        note.blob,
        note.durationMs,
        handler.onEvent,
        fresh ? { newConversation: true } : {},
      );
      // Nothing was stored (silent or unreadable note): the error explains why.
      if (!handler.accepted()) drop();
    } catch (err) {
      drop();
      setNotice(err instanceof Error ? err.message : 'Voice note could not be sent');
    } finally {
      setStreaming(null);
      setSending(false);
    }
  }

  /**
   * Handles the streamed reply to one visitor message: swaps in the stored message, streams the
   * answer, and shows follow-ups, help chips and status changes.
   */
  function replyHandler(pendingId: string, localAudioUrl?: string) {
    let answerId: string | null = null;
    let accepted = false;
    const onEvent = (e: ChatStreamEvent) => {
      switch (e.type) {
        case 'accepted':
          accepted = true;
          setMessages((m) => m.map((x) => (x.id === pendingId ? { ...x, id: e.messageId } : x)));
          break;
        case 'delta':
          setStreaming((s) => (s ?? '') + e.text);
          break;
        case 'discarded':
          setStreaming(null);
          break;
        case 'suggestions':
          if (answerId) setFollowUps({ forId: answerId, items: e.items });
          break;
        case 'no_answer':
          if (answerId) setHelpFor(answerId);
          break;
        case 'message': {
          if (e.message.senderType === 'ai') answerId = e.message.id;
          setStreaming(null);
          // The visitor's own voice note comes back with its transcript: update it in place.
          const incoming = localAudioUrl ? { ...e.message, localAudioUrl } : e.message;
          setMessages((m) =>
            m.some((x) => x.id === e.message.id)
              ? m.map((x) => (x.id === e.message.id ? { ...x, ...incoming } : x))
              : [...m, e.message],
          );
          break;
        }
        case 'handoff':
        case 'waiting':
          setStatus(e.status);
          break;
        case 'error':
          setStreaming(null);
          setNotice(e.message);
          break;
        case 'done':
          setStreaming(null);
          break;
      }
    };
    return { onEvent, accepted: () => accepted };
  }

  function askSay(text: string, role: 'ai' | 'contact') {
    const afterId = messages[messages.length - 1]?.id ?? null;
    setAskLog((log) => [
      ...log,
      {
        afterId,
        message: {
          id: localId(),
          senderType: role,
          senderName: null,
          body: text,
          citations: [],
          createdAt: new Date().toISOString(),
        },
      },
    ]);
  }

  function askNext(ask: AskDetailsConfig, known: VisitorInfo, after: AskStep | null) {
    const next = nextAskStep(ask, known, after);
    const first = firstNameOf(known.name);
    setAskValue('');
    setAskErr(null);
    if (next) {
      askSay(askText(next, first), 'ai');
      setAskStep(next);
      return;
    }
    if (after) askSay(askText('end', first), 'ai');
    setAskStep(null);
    setAskState('done');
    saveAskState(widgetKey, 'done');
  }

  // After the visitor's Nth question has been answered, ask for their details (M5 lead capture).
  useEffect(() => {
    const ask = config?.widget.askDetails;
    if (!ask?.enabled || askState !== 'idle' || askStep || !token) return;
    if (sending || streaming !== null || formPending()) return;
    if (status && status !== 'ai_active') return;
    const asked = messages.filter((m) => m.senderType === 'contact').length;
    const last = messages[messages.length - 1];
    if (asked < Math.max(ask.afterQuestions, askDeferUntil.current)) return;
    if (last?.senderType !== 'ai' || askChecking.current) return;
    // The assistant may have learnt their details since the chat loaded (a form, a call, a
    // booking): check with the server first, and never ask for what it already has.
    askChecking.current = true;
    void api
      .history(token)
      .then((h) => h.visitor ?? visitor)
      .catch(() => visitor)
      .then((fresh) => {
        askChecking.current = false;
        setVisitor(fresh);
        askNext(ask, fresh, null);
      });
    // askNext and formPending read the current state; the listed values drive re-checks.
  }, [config, askState, askStep, token, sending, streaming, status, messages, visitor]);

  useEffect(() => {
    if (askStep) askInputRef.current?.focus();
  }, [askStep]);

  async function answerAsk(skip: boolean) {
    if (askMode === 'call') return answerCall(skip);
    const ask = config?.widget.askDetails;
    if (!ask || !askStep || !token || askBusy) return;
    if (skip) {
      askSay('Skip', 'contact');
      askNext(ask, visitor, askStep);
      return;
    }
    const problem = askError(askStep, askValue);
    if (problem) return setAskErr(problem);
    setAskBusy(true);
    try {
      await api.lead(token, { source: 'in_chat', [askStep]: askValue.trim() });
      askSay(askValue.trim(), 'contact');
      const known = {
        ...visitor,
        ...(askStep === 'name' ? { name: askValue.trim() } : {}),
        ...(askStep === 'phone' ? { hasPhone: true } : {}),
        ...(askStep === 'email' ? { hasEmail: true } : {}),
      };
      setVisitor(known);
      askNext(ask, known, askStep);
    } catch (err) {
      const fieldErrors = (err as { fieldErrors?: Record<string, string> }).fieldErrors ?? {};
      setAskErr(fieldErrors[askStep] ?? (err instanceof Error ? err.message : 'Please try again'));
    } finally {
      setAskBusy(false);
    }
  }

  // "Recent chats": refreshed whenever the home screen shows.
  useEffect(() => {
    if (!open || view !== 'home' || !token) return;
    let cancelled = false;
    // Best-effort: the home screen works without it.
    Promise.resolve()
      .then(() => api.conversations(token))
      .then((list) => {
        if (!cancelled) setRecent(Array.isArray(list) ? list : []);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, view, token, api]);

  /** Opens a chat from "Recent chats": the current one continues; an ended one is read-only. */
  async function openChat(c: WidgetConversationSummary) {
    if (!token) return;
    if (c.current && c.status !== 'closed') {
      setOldChat(null);
      if (freshChat.current) {
        // They started a new chat but went back to this one: it carries on, with its messages.
        try {
          const h = await api.history(token, c.id);
          freshChat.current = false;
          setMessages(h.messages);
          setStatus(h.status);
        } catch {
          setNotice('That chat could not be opened.');
          return;
        }
      }
      setView('chat');
      return;
    }
    try {
      const h = await api.history(token, c.id);
      setOldChat({ id: c.id, messages: h.messages });
      setView('chat');
    } catch {
      setNotice('That chat could not be opened.');
    }
  }

  /** "New chat": a clean slate; the server starts a new conversation with the next message. */
  function newChat() {
    // Leaving an answered chat: ask for its rating first (once), then start the new one.
    if (!oldChat && csat === 'hidden' && askRating(startNewChat)) return;
    startNewChat();
  }

  function startNewChat() {
    csatThen.current = null;
    freshChat.current = true;
    setOldChat(null);
    setMessages([]);
    setFollowUps(null);
    setAskLog([]);
    setAskStep(null);
    setAskMode('details');
    setStatus(null);
    setAgentName(null);
    setCsat('hidden');
    setNotice(null);
    setDraft('');
    setView('chat');
    askDeferUntil.current = 0;
  }

  /** Starts a voice call with the assistant; transcripts land in this chat. */
  function startVoice() {
    if (!token || voiceRef.current) return;
    // After "New chat", the call opens the new conversation.
    const fresh = freshChat.current;
    freshChat.current = false;
    setOldChat(null);
    setView('chat');
    setVoiceCall({
      state: 'connecting',
      user: '',
      ai: '',
      maxSeconds: 0,
      startedAt: Date.now(),
      muted: false,
      endedMessage: null,
      endedReason: null,
    });
    const client = new VoiceClient(
      apiUrl,
      token,
      {
        onState: (state) => setVoiceCall((v) => v && { ...v, state }),
        onReady: (maxSeconds) =>
          setVoiceCall((v) => v && { ...v, maxSeconds, startedAt: Date.now() }),
        onTranscript: (role, text) =>
          setVoiceCall(
            (v) => v && (role === 'user' ? { ...v, user: text, ai: '' } : { ...v, ai: text }),
          ),
        onMessage: (message) =>
          setMessages((m) => (m.some((x) => x.id === message.id) ? m : [...m, message])),
        onEnded: (reason, message) => {
          voiceRef.current = null;
          setVoiceCall(
            (v) => v && { ...v, state: 'ended', endedMessage: message, endedReason: reason },
          );
          if (reason === 'handoff') setStatus('pending_human');
        },
        onLevel: (level) => orbRef.current?.style.setProperty('--vs-level', level.toFixed(2)),
      },
      fresh,
    );
    voiceRef.current = client;
    void client.start();
  }

  /** Calls the team from the browser: rings their dashboards, then talks over WebRTC. */
  function startTeamCall() {
    if (!token || teamCallRef.current || voiceRef.current) return;
    setOldChat(null);
    setView('chat');
    setTeamCall({
      state: 'ringing',
      agentName: null,
      startedAt: Date.now(),
      muted: false,
      endedReason: null,
      endedMessage: null,
    });
    const client = new TeamCall(
      {
        start: () => api.startCall(token, realtime.current?.socketId() ?? null),
        end: (callId) => api.endCall(token, callId),
        signal: (callId, signal) => realtime.current?.callSignal(callId, signal),
      },
      {
        onState: (state) => setTeamCall((c) => c && { ...c, state }),
        onAccepted: (name) => setTeamCall((c) => c && { ...c, agentName: name }),
        onLive: () => setTeamCall((c) => c && { ...c, startedAt: Date.now() }),
        onEnded: (reason, message) => {
          teamCallRef.current = null;
          setTeamCall(
            (c) => c && { ...c, state: 'ended', endedReason: reason, endedMessage: message },
          );
        },
      },
    );
    teamCallRef.current = client;
    void client.start();
  }

  function toggleTeamMute() {
    setTeamCall((c) => {
      if (!c) return c;
      teamCallRef.current?.setMuted(!c.muted);
      return { ...c, muted: !c.muted };
    });
  }

  function toggleMute() {
    setVoiceCall((v) => {
      if (!v) return v;
      voiceRef.current?.setMuted(!v.muted);
      return { ...v, muted: !v.muted };
    });
  }

  /** The call icon: take the visitor's number (and email if unknown) for a callback. */
  function startCall() {
    setView('chat');
    setAskMode('call');
    setAskValue('');
    setAskErr(null);
    callPhone.current = '';
    askSay(
      firstName
        ? `Sure, ${firstName}! What number should our team call you on? 📞`
        : 'Sure! What number should our team call you on? 📞',
      'ai',
    );
    setAskStep('phone');
  }

  async function finishCall(email: string | null) {
    if (!token) return;
    setAskBusy(true);
    try {
      const res = await api.callback(token, {
        phone: callPhone.current,
        ...(email ? { email } : {}),
      });
      setVisitor((v) => ({ ...v, hasPhone: true, hasEmail: v.hasEmail || Boolean(email) }));
      askSay(
        `${firstName ? `Thanks, ${firstName}!` : 'Thanks!'} We'll call you on ${res.phone} ${res.promise}. 📞`,
        'ai',
      );
      setAskStep(null);
      setAskMode('details');
      // They just gave their number: don't follow up at once with the details questions.
      askDeferUntil.current = messages.filter((m) => m.senderType === 'contact').length + 2;
    } catch (err) {
      const fieldErrors = (err as { fieldErrors?: Record<string, string> }).fieldErrors ?? {};
      if (fieldErrors.phone) {
        setAskStep('phone');
        setAskValue(callPhone.current);
        setAskErr(fieldErrors.phone);
      } else {
        setAskErr(fieldErrors.email ?? (err instanceof Error ? err.message : 'Please try again'));
      }
    } finally {
      setAskBusy(false);
    }
  }

  async function answerCall(skip: boolean) {
    if (!askStep) return;
    if (askStep === 'phone') {
      const problem = askError('phone', askValue);
      if (problem) return setAskErr(problem);
      callPhone.current = askValue.trim();
      askSay(callPhone.current, 'contact');
      setAskValue('');
      setAskErr(null);
      if (!visitor.hasEmail) {
        askSay('And your email, so we can send you the details after the call? (optional)', 'ai');
        setAskStep('email');
        return;
      }
      return finishCall(null);
    }
    if (skip) {
      askSay('Skip', 'contact');
      return finishCall(null);
    }
    const problem = askError('email', askValue);
    if (problem) return setAskErr(problem);
    askSay(askValue.trim(), 'contact');
    return finishCall(askValue.trim());
  }

  function dismissAsk() {
    if (askMode === 'call') {
      setAskStep(null);
      setAskMode('details');
      setAskErr(null);
      askSay('No problem! Ask me anything here instead. 😊', 'ai');
      return;
    }
    setAskStep(null);
    setAskErr(null);
    askSay('No problem! Ask me anything else. 😊', 'ai');
    setAskState('dismissed');
    saveAskState(widgetKey, 'dismissed');
  }

  if (phase === 'unavailable') return null;
  const widget = config?.widget;
  const avatar = widget?.avatarUrl ?? null;
  const logoBg = widget?.logoBackground ?? '#ffffff';
  const side = position ?? widget?.position ?? 'right';
  const primary = widget?.primaryColor ?? '#111827';
  const firstName = firstNameOf(visitor.name);
  /** A form opened mid-call is shown inside the call screen. */
  const formInCall = Boolean(detailsFor && voiceCall && voiceCall.state !== 'ended');
  const greeting = widget?.greeting ? personalGreeting(widget.greeting, firstName) : undefined;
  const showFollowUps =
    Boolean(followUps?.items.length) &&
    !sending &&
    streaming === null &&
    (!status || status === 'ai_active');
  // The details-asking exchange sits right after the message it followed (no clock mixing).
  const askAfter = (id: string | null) =>
    askLog.filter((a) => a.afterId === id).map((a) => a.message);
  const suggestions = widget?.suggestedQuestions ?? [];
  const showSuggestions = messages.length === 0 && suggestions.length > 0;
  const waitingForHuman = status === 'pending_human' || status === 'human_active';
  const lastMessage = [...messages].reverse().find((m) => m.senderType !== 'system');
  const botName = config?.botName ?? 'Assistant';
  const voiceOn = Boolean(config?.widget.voice?.enabled) && voiceSupported();
  const teamCallOn = Boolean(config?.widget.call?.browser) && teamCallSupported();
  const statusLineCurrent =
    status === 'human_active' && agentName
      ? `Chatting with ${agentName}`
      : waitingForHuman
        ? 'Connecting you with our team'
        : 'AI assistant · replies instantly';

  const bubbleFor = (m: WidgetMessage, i: number, list: WidgetMessage[] = messages) => {
    const mine = m.senderType === 'contact';
    const system = m.senderType === 'system';
    const prev = list[i - 1];
    // Consecutive messages from the same sender are grouped under one avatar.
    const firstOfGroup = !prev || prev.senderType !== m.senderType;
    return (
      <div
        key={m.id}
        class={`vs-msg ${mine ? 'vs-msg-me' : system ? 'vs-msg-system' : 'vs-msg-bot'}${firstOfGroup ? ' vs-first' : ''}`}
      >
        {!mine && !system && (
          <div class="vs-gutter">
            {firstOfGroup &&
              (m.senderType === 'agent' ? (
                <span class="vs-avatar vs-avatar-sm vs-avatar-agent">{initial(m.senderName)}</span>
              ) : (
                <BotAvatar src={avatar} bg={logoBg} size="sm" />
              ))}
          </div>
        )}
        <div class="vs-stack">
          {m.senderType === 'agent' && m.senderName && firstOfGroup && (
            <div class="vs-sender">{m.senderName}</div>
          )}
          {m.voiceNote ? (
            <div class="vs-bubble vs-bubble-note">
              <NotePlayer
                durationMs={m.voiceNote.durationMs}
                src={m.localAudioUrl}
                load={() => api.voiceNoteUrl(token ?? '', m.id)}
              />
              <div class="vs-note-text">
                {m.body || (m.id.startsWith('local-') ? 'Listening to your note…' : '')}
              </div>
            </div>
          ) : (
            <div class="vs-bubble">{mine ? m.body : renderText(m.body, m.citations)}</div>
          )}
          {m.blocks && m.blocks.length > 0 && (
            <MessageBlocks
              blocks={m.blocks}
              active={
                list === messages &&
                i === list.length - 1 &&
                !sending &&
                !oldChat &&
                status !== 'closed'
              }
              onPick={(text) => void send(text)}
              onDetails={(block) => setDetailsFor({ id: m.id, block })}
            />
          )}
          {m.citations.some((c) => c.url) && (
            <div class="vs-sources">
              Sources:{' '}
              {m.citations
                .filter((c) => c.url)
                .map((c, j) => (
                  <span key={c.n}>
                    {j > 0 && ', '}
                    <a href={c.url!} target="_blank" rel="noopener noreferrer">
                      {c.title ?? c.url}
                    </a>
                  </span>
                ))}
            </div>
          )}
          {m.senderType === 'ai' &&
            !m.id.startsWith('local-') &&
            (m.feedback || m.id === lastAnswerId(list)) && (
              <div class="vs-rate" role="group" aria-label="Was this helpful?">
                {m.feedback === 'down' ? (
                  <span class="vs-rate-thanks">Thanks, we&apos;ll use this to improve.</span>
                ) : m.feedback === 'up' ? (
                  <span class="vs-rate-thanks">Glad it helped!</span>
                ) : null}
                <button
                  type="button"
                  class={`vs-rate-btn${m.feedback === 'up' ? ' is-on' : ''}`}
                  aria-label="Helpful"
                  aria-pressed={m.feedback === 'up'}
                  onClick={() => rateAnswer(m.id, 'up')}
                >
                  <Svg size={14}>{Icon.thumbUp}</Svg>
                </button>
                <button
                  type="button"
                  class={`vs-rate-btn${m.feedback === 'down' ? ' is-on' : ''}`}
                  aria-label="Not helpful"
                  aria-pressed={m.feedback === 'down'}
                  onClick={() => rateAnswer(m.id, 'down')}
                >
                  <Svg size={14}>{Icon.thumbDown}</Svg>
                </button>
              </div>
            )}
          {!system && timeOf(m.createdAt) && (
            <div class="vs-time">
              {m.voice && (
                <span class="vs-voice-tag">
                  <Svg size={11}>{Icon.mic}</Svg> Voice
                </span>
              )}
              {m.voice && ' · '}
              {timeOf(m.createdAt)}
            </div>
          )}
        </div>
      </div>
    );
  };

  const typingDots = (
    <span class="vs-typing" aria-label="Typing">
      <i />
      <i />
      <i />
    </span>
  );

  return (
    <div class={`vs-root${open ? ' is-open' : ''}`} data-position={side} style={brandVars(primary)}>
      {mounted && (
        <section
          class={`vs-panel${open ? '' : ' is-closing'}`}
          role="dialog"
          aria-label={widget?.title ?? 'Chat'}
          onKeyDown={(e) => {
            if (e.key === 'Escape') toggle();
          }}
        >
          {view === 'home' ? (
            <div class="vs-home">
              <div class="vs-hero">
                <div class="vs-hero-glow" aria-hidden="true" />
                <div class="vs-hero-top">
                  <span class="vs-avatars">
                    {avatar ? (
                      <LogoPlate src={avatar} bg={logoBg} />
                    ) : (
                      <BotAvatar size="lg" online />
                    )}
                  </span>
                  <button
                    type="button"
                    class="vs-icon-btn"
                    aria-label="Close chat"
                    onClick={toggle}
                  >
                    <Svg>{Icon.down}</Svg>
                  </button>
                </div>
                {config?.orgName && <div class="vs-hero-org">{config.orgName}</div>}
                <h2 class="vs-hero-title">
                  {firstName ? `Welcome back, ${firstName} 👋` : (widget?.title ?? 'Chat with us')}
                </h2>
                <p class="vs-hero-sub">
                  {widget?.subtitle ||
                    `Ask ${botName} anything. Instant answers, any time, and our team is here if you need a person.`}
                </p>
              </div>
              <div class="vs-home-body">
                <button type="button" class="vs-card vs-start" onClick={() => startChat()}>
                  <span class="vs-card-icon">
                    <Svg>{Icon.chat}</Svg>
                  </span>
                  <span class="vs-card-text">
                    <span class="vs-card-title">
                      {lastMessage ? 'Continue the conversation' : 'Chat with us now'}
                    </span>
                    <span class="vs-card-sub">
                      {lastMessage
                        ? lastMessage.body.slice(0, 80)
                        : waitingForHuman
                          ? 'Our team will reply here'
                          : 'Usually replies in seconds'}
                    </span>
                  </span>
                  <span class="vs-card-go">
                    <Svg size={18}>{Icon.arrow}</Svg>
                  </span>
                </button>
                {lastMessage && (
                  <button
                    type="button"
                    class="vs-home-new"
                    onClick={() => {
                      // In the chat, so a due rating of the current chat is seen first.
                      setView('chat');
                      newChat();
                    }}
                  >
                    <Svg size={16}>{Icon.compose}</Svg>
                    Start a new chat
                  </button>
                )}
                {voiceOn && (
                  <button
                    type="button"
                    class="vs-card vs-start vs-voice-card"
                    onClick={startVoice}
                    disabled={!token}
                  >
                    <span class="vs-card-icon">
                      <Svg>{Icon.mic}</Svg>
                    </span>
                    <span class="vs-card-text">
                      <span class="vs-card-title">Talk to {botName}</span>
                      <span class="vs-card-sub">Ask out loud and hear the answer</span>
                    </span>
                    <span class="vs-card-go">
                      <Svg size={18}>{Icon.arrow}</Svg>
                    </span>
                  </button>
                )}
                {teamCallOn && (
                  <button
                    type="button"
                    class="vs-card vs-start vs-call-card"
                    onClick={startTeamCall}
                    disabled={!token}
                  >
                    <span class="vs-card-icon">
                      <Svg>{Icon.phone}</Svg>
                    </span>
                    <span class="vs-card-text">
                      <span class="vs-card-title">Voice call with our team</span>
                      <span class="vs-card-sub">Free, right here in your browser</span>
                    </span>
                    <span class="vs-card-go">
                      <Svg size={18}>{Icon.arrow}</Svg>
                    </span>
                  </button>
                )}
                {config?.widget.call?.enabled && (
                  <button type="button" class="vs-card vs-start vs-call-card" onClick={startCall}>
                    <span class="vs-card-icon">
                      <Svg>{Icon.phone}</Svg>
                    </span>
                    <span class="vs-card-text">
                      <span class="vs-card-title">Request a call back</span>
                      <span class="vs-card-sub">
                        Leave your number, we&apos;ll call you {config.widget.call.promise}
                      </span>
                    </span>
                    <span class="vs-card-go">
                      <Svg size={18}>{Icon.arrow}</Svg>
                    </span>
                  </button>
                )}
                {whatsAppButton && (
                  <button
                    type="button"
                    class="vs-card vs-start vs-wa-card"
                    onClick={() => void openWhatsApp()}
                  >
                    <span class="vs-card-icon">
                      <Svg>{Icon.chat}</Svg>
                    </span>
                    <span class="vs-card-text">
                      <span class="vs-card-title">{whatsAppButton}</span>
                      <span class="vs-card-sub">Chat with us on WhatsApp instead</span>
                    </span>
                    <span class="vs-card-go">
                      <Svg size={18}>{Icon.arrow}</Svg>
                    </span>
                  </button>
                )}
                {recent.length > 0 && (
                  <div class="vs-card vs-faqs vs-recent">
                    <div class="vs-faqs-title">Recent chats</div>
                    {recent.slice(0, 5).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        class="vs-faq vs-recent-item"
                        onClick={() => void openChat(c)}
                      >
                        <span class="vs-recent-text">
                          <span class="vs-recent-preview">
                            {c.previewFrom === 'contact' ? 'You: ' : ''}
                            {c.preview ?? 'Chat'}
                          </span>
                          <span class="vs-recent-meta">
                            {c.lastMessageAt ? ago(c.lastMessageAt) : ''}
                            {c.status === 'closed' ? ' · Ended' : c.current ? ' · Open' : ''}
                          </span>
                        </span>
                        <Svg size={16}>{Icon.arrow}</Svg>
                      </button>
                    ))}
                  </div>
                )}
                {suggestions.length > 0 && (
                  <div class="vs-card vs-faqs">
                    <div class="vs-faqs-title">Popular questions</div>
                    {suggestions.map((q) => (
                      <button key={q} type="button" class="vs-faq" onClick={() => startChat(q)}>
                        <span>{q}</span>
                        <Svg size={16}>{Icon.arrow}</Svg>
                      </button>
                    ))}
                  </div>
                )}
                {notice && (
                  <div class="vs-notice" role="alert">
                    {notice}
                  </div>
                )}
              </div>
              <div class="vs-brand">
                Powered by <strong>VixSync</strong>
              </div>
            </div>
          ) : (
            <div class="vs-chat">
              {teamCall && (
                <div
                  class={`vs-voice is-${TEAM_CALL_LOOK[teamCall.state]}`}
                  role="dialog"
                  aria-label="Call with our team"
                >
                  <div class="vs-voice-top">
                    <span class="vs-voice-name">
                      {teamCall.agentName ?? config?.orgName ?? 'Our team'}
                    </span>
                    <span class="vs-voice-status" aria-live="polite">
                      {teamCall.state === 'ringing'
                        ? 'Calling…'
                        : teamCall.state === 'connecting'
                          ? 'Connecting…'
                          : teamCall.state === 'ended'
                            ? 'Call ended'
                            : `${teamCall.muted ? 'Muted · ' : ''}${clock(Date.now() - teamCall.startedAt)}`}
                    </span>
                  </div>
                  <div class="vs-orb" aria-hidden="true">
                    <span class="vs-orb-ring" />
                    <span class="vs-orb-ring vs-orb-ring-2" />
                    <span class="vs-orb-core">
                      {avatar ? (
                        <LogoPlate src={avatar} bg={logoBg} />
                      ) : (
                        <Svg size={34}>{Icon.phone}</Svg>
                      )}
                    </span>
                  </div>
                  <div class="vs-voice-captions" aria-live="polite">
                    {teamCall.state === 'ended' ? (
                      <p class="vs-voice-ai">
                        {teamCall.endedMessage ?? TEAM_CALL_ENDED[teamCall.endedReason ?? 'ended']}
                      </p>
                    ) : (
                      <p class="vs-voice-hint">
                        {teamCall.state === 'ringing'
                          ? 'Ringing our team. Allow the microphone if your browser asks.'
                          : teamCall.state === 'connecting'
                            ? `${teamCall.agentName ?? 'Someone'} picked up. Connecting…`
                            : `You're talking with ${teamCall.agentName ?? 'our team'}.`}
                      </p>
                    )}
                  </div>
                  {teamCall.state === 'ended' ? (
                    <div class="vs-voice-actions vs-voice-after">
                      {teamCall.endedReason === 'missed' && config?.widget.call?.enabled ? (
                        <button
                          type="button"
                          class="vs-voice-back"
                          onClick={() => {
                            setTeamCall(null);
                            startCall();
                          }}
                        >
                          <Svg size={18}>{Icon.phone}</Svg> Request a call back
                        </button>
                      ) : (
                        teamCall.endedReason !== 'mic' && (
                          <button
                            type="button"
                            class="vs-voice-back"
                            onClick={() => {
                              setTeamCall(null);
                              startTeamCall();
                            }}
                          >
                            <Svg size={18}>{Icon.phone}</Svg> Call again
                          </button>
                        )
                      )}
                      <button type="button" class="vs-voice-link" onClick={() => setTeamCall(null)}>
                        Back to chat
                      </button>
                    </div>
                  ) : (
                    <div class="vs-voice-actions">
                      <button
                        type="button"
                        class={`vs-voice-btn${teamCall.muted ? ' is-on' : ''}`}
                        aria-label={teamCall.muted ? 'Unmute' : 'Mute'}
                        aria-pressed={teamCall.muted}
                        onClick={toggleTeamMute}
                        disabled={teamCall.state !== 'live'}
                      >
                        <Svg size={22}>{teamCall.muted ? Icon.micOff : Icon.mic}</Svg>
                      </button>
                      <button
                        type="button"
                        class="vs-voice-btn vs-voice-end"
                        aria-label="End call"
                        onClick={() => teamCallRef.current?.hangUp()}
                      >
                        <Svg size={26}>{Icon.hangup}</Svg>
                      </button>
                    </div>
                  )}
                </div>
              )}
              {voiceCall && (
                <div
                  class={`vs-voice is-${voiceCall.state}${formInCall ? ' has-form' : ''}`}
                  role="dialog"
                  aria-label={`Voice call with ${botName}`}
                >
                  <div class="vs-voice-top">
                    <span class="vs-voice-name">{botName}</span>
                    <span class="vs-voice-status" aria-live="polite">
                      {voiceCall.state === 'connecting'
                        ? 'Connecting…'
                        : voiceCall.state === 'speaking'
                          ? 'Speaking'
                          : voiceCall.state === 'thinking'
                            ? 'Checking…'
                            : voiceCall.state === 'ended'
                              ? 'Call ended'
                              : voiceCall.muted
                                ? 'Muted'
                                : 'Listening…'}
                      {callLive && voiceCall.maxSeconds > 0 && (
                        <span class="vs-voice-time">
                          {' · '}
                          {clock(Date.now() - voiceCall.startedAt)} /{' '}
                          {clock(voiceCall.maxSeconds * 1000)}
                        </span>
                      )}
                    </span>
                  </div>
                  <div class="vs-orb" ref={orbRef} aria-hidden="true">
                    <span class="vs-orb-ring" />
                    <span class="vs-orb-ring vs-orb-ring-2" />
                    <span class="vs-orb-core">
                      {avatar ? (
                        <LogoPlate src={avatar} bg={logoBg} />
                      ) : (
                        <Svg size={34}>{Icon.spark}</Svg>
                      )}
                    </span>
                  </div>
                  {formInCall && detailsFor && (
                    <DetailsSheet
                      key={detailsFor.id}
                      inline
                      block={detailsFor.block}
                      onSubmit={submitDetails}
                      onClose={() => setDetailsFor(null)}
                      optInLabel={whatsAppOptInLabel}
                      callOptInLabel={callOptInLabel}
                    />
                  )}
                  <div class="vs-voice-captions" aria-live="polite">
                    {voiceCall.state === 'ended' ? (
                      <p class="vs-voice-ai">{voiceCall.endedMessage}</p>
                    ) : voiceCall.ai ? (
                      <p class="vs-voice-ai">{voiceCall.ai}</p>
                    ) : voiceCall.user ? (
                      <p class="vs-voice-user">“{voiceCall.user}”</p>
                    ) : (
                      <p class="vs-voice-hint">
                        {voiceCall.state === 'connecting'
                          ? 'Allow the microphone if your browser asks.'
                          : 'Go ahead, ask your question.'}
                      </p>
                    )}
                  </div>
                  {voiceCall.state === 'ended' ? (
                    <div class="vs-voice-actions vs-voice-after">
                      {voiceCall.endedReason === 'handoff' ? (
                        <button
                          type="button"
                          class="vs-voice-back"
                          onClick={() => {
                            setVoiceCall(null);
                            newChat();
                            startVoice();
                          }}
                        >
                          <Svg size={18}>{Icon.mic}</Svg> Talk in a new chat
                        </button>
                      ) : (
                        canCallAgain(voiceCall.endedReason) && (
                          <button
                            type="button"
                            class="vs-voice-back"
                            onClick={() => {
                              setVoiceCall(null);
                              startVoice();
                            }}
                          >
                            <Svg size={18}>{Icon.mic}</Svg> Call again
                          </button>
                        )
                      )}
                      <button
                        type="button"
                        class="vs-voice-link"
                        onClick={() => setVoiceCall(null)}
                      >
                        Back to chat
                      </button>
                    </div>
                  ) : (
                    <div class="vs-voice-actions">
                      <button
                        type="button"
                        class={`vs-voice-btn${voiceCall.muted ? ' is-on' : ''}`}
                        aria-label={voiceCall.muted ? 'Unmute' : 'Mute'}
                        aria-pressed={voiceCall.muted}
                        onClick={toggleMute}
                        disabled={voiceCall.state === 'connecting'}
                      >
                        <Svg size={22}>{voiceCall.muted ? Icon.micOff : Icon.mic}</Svg>
                      </button>
                      <button
                        type="button"
                        class="vs-voice-btn vs-voice-end"
                        aria-label="End call"
                        onClick={() => voiceRef.current?.stop()}
                      >
                        <Svg size={26}>{Icon.hangup}</Svg>
                      </button>
                    </div>
                  )}
                </div>
              )}
              <header class="vs-header">
                <button
                  type="button"
                  class="vs-icon-btn"
                  aria-label="Back"
                  onClick={() => {
                    setOldChat(null);
                    setView('home');
                  }}
                >
                  <Svg>{Icon.back}</Svg>
                </button>
                {status === 'human_active' && agentName ? (
                  <span class="vs-avatar vs-avatar-md vs-avatar-agent">
                    {initial(agentName)}
                    <span class="vs-online" aria-hidden="true" />
                  </span>
                ) : (
                  <BotAvatar src={avatar} bg={logoBg} online />
                )}
                <div class="vs-header-text">
                  <div class="vs-title">{botName}</div>
                  {config && (
                    <div class="vs-subtitle">{oldChat ? 'Earlier chat' : statusLineCurrent}</div>
                  )}
                </div>
                {voiceOn && !oldChat && (
                  <button
                    type="button"
                    class="vs-icon-btn"
                    aria-label={`Talk to ${botName}`}
                    title="Talk (voice)"
                    onClick={startVoice}
                    disabled={!token}
                  >
                    <Svg size={19}>{Icon.mic}</Svg>
                  </button>
                )}
                {config?.widget.call?.enabled && (
                  <button
                    type="button"
                    class="vs-icon-btn"
                    aria-label="Request a call back"
                    title="Request a call back"
                    onClick={startCall}
                  >
                    <Svg size={19}>{Icon.phone}</Svg>
                  </button>
                )}
                {messages.length > 0 && (
                  <button
                    type="button"
                    class="vs-icon-btn"
                    aria-label="New chat"
                    title="New chat"
                    onClick={newChat}
                  >
                    <Svg size={19}>{Icon.compose}</Svg>
                  </button>
                )}
                <button type="button" class="vs-icon-btn" aria-label="Close chat" onClick={toggle}>
                  <Svg>{Icon.down}</Svg>
                </button>
              </header>
              {formPending() && config?.widget.preChat ? (
                <div class="vs-log vs-log-form">
                  {greeting && (
                    <div class="vs-msg vs-msg-bot vs-first">
                      <div class="vs-gutter">
                        <BotAvatar src={avatar} bg={logoBg} size="sm" />
                      </div>
                      <div class="vs-stack">
                        <div class="vs-bubble">{greeting}</div>
                      </div>
                    </div>
                  )}
                  <PreChatForm
                    form={config.widget.preChat}
                    ready={Boolean(token)}
                    onSubmit={submitLead}
                    optInLabel={whatsAppOptInLabel}
                    callOptInLabel={callOptInLabel}
                  />
                  {notice && (
                    <div class="vs-notice" role="alert">
                      {notice}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div
                    class="vs-log"
                    ref={logRef}
                    role="log"
                    aria-live="polite"
                    aria-relevant="additions"
                  >
                    {greeting && (
                      <div class="vs-msg vs-msg-bot vs-first">
                        <div class="vs-gutter">
                          <BotAvatar src={avatar} bg={logoBg} size="sm" />
                        </div>
                        <div class="vs-stack">
                          <div class="vs-bubble">{greeting}</div>
                        </div>
                      </div>
                    )}
                    {oldChat && oldChat.messages.map((m, i) => bubbleFor(m, i, oldChat.messages))}
                    {!oldChat && askAfter(null).map((m, i) => bubbleFor(m, i))}
                    {!oldChat &&
                      messages.map((m, i) => (
                        <Fragment key={m.id}>
                          {bubbleFor(m, i)}
                          {m.id === followUps?.forId && (
                            <>
                              {showFollowUps && followUps && (
                                <div
                                  class="vs-followups"
                                  role="group"
                                  aria-label="Suggested questions"
                                >
                                  {followUps.items.map((q, i) => (
                                    <button
                                      key={q}
                                      type="button"
                                      class="vs-followup"
                                      style={{ animationDelay: `${i * 70}ms` }}
                                      onClick={() => {
                                        if (askStep) {
                                          setAskStep(null);
                                          askDeferUntil.current =
                                            messages.filter((m) => m.senderType === 'contact')
                                              .length + 2;
                                        }
                                        void send(q);
                                      }}
                                    >
                                      <span>{q}</span>
                                      <Svg size={15}>{Icon.arrow}</Svg>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                          {m.id === helpFor &&
                            status !== 'pending_human' &&
                            status !== 'human_active' && (
                              <div class="vs-followups" role="group" aria-label="More help">
                                <button
                                  type="button"
                                  class="vs-followup"
                                  onClick={() =>
                                    void send("I'd like to talk to someone from your team", {
                                      handoff: true,
                                    })
                                  }
                                >
                                  <span>Talk to our team</span>
                                  <Svg size={15}>{Icon.arrow}</Svg>
                                </button>
                                {config?.widget.call?.enabled && (
                                  <button
                                    type="button"
                                    class="vs-followup"
                                    style={{ animationDelay: '70ms' }}
                                    onClick={() => {
                                      setHelpFor(null);
                                      startCall();
                                    }}
                                  >
                                    <span>Request a call back</span>
                                    <Svg size={15}>{Icon.phone}</Svg>
                                  </button>
                                )}
                              </div>
                            )}
                          {askAfter(m.id).map((a, j) => bubbleFor(a, j))}
                        </Fragment>
                      ))}
                    {streaming !== null && (
                      <div class="vs-msg vs-msg-bot vs-first">
                        <div class="vs-gutter">
                          <BotAvatar src={avatar} bg={logoBg} size="sm" />
                        </div>
                        <div class="vs-stack">
                          <div class="vs-bubble">
                            {streaming ? renderText(streaming) : typingDots}
                          </div>
                        </div>
                      </div>
                    )}
                    {typingName && streaming === null && (
                      <div class="vs-msg vs-msg-bot vs-first">
                        <div class="vs-gutter">
                          <span class="vs-avatar vs-avatar-sm vs-avatar-agent">
                            {initial(typingName)}
                          </span>
                        </div>
                        <div class="vs-stack">
                          <div class="vs-sender">{typingName} is typing…</div>
                          <div class="vs-bubble vs-bubble-typing">{typingDots}</div>
                        </div>
                      </div>
                    )}
                    {sending && streaming === null && (
                      <div class="vs-msg vs-msg-bot vs-first" aria-hidden="true">
                        <div class="vs-gutter">
                          <BotAvatar src={avatar} bg={logoBg} size="sm" />
                        </div>
                        <div class="vs-stack">
                          <div class="vs-bubble vs-bubble-typing">{typingDots}</div>
                        </div>
                      </div>
                    )}
                    {csat !== 'hidden' && (
                      <div class="vs-msg vs-msg-system">
                        {csat === 'asking' ? (
                          <div class="vs-csat" role="group" aria-label="Rate this conversation">
                            <div class="vs-csat-title">How was your chat?</div>
                            <div class="vs-csat-stars">
                              {[1, 2, 3, 4, 5].map((n) => (
                                <button
                                  key={n}
                                  type="button"
                                  class="vs-star"
                                  aria-label={`${n} out of 5`}
                                  onClick={() => void rate(n)}
                                >
                                  ★
                                </button>
                              ))}
                            </div>
                            <button
                              type="button"
                              class="vs-csat-skip"
                              onClick={() => finishRating('hidden')}
                            >
                              Not now
                            </button>
                          </div>
                        ) : csat === 'comment' ? (
                          <div class="vs-csat" role="group" aria-label="Tell us more">
                            <div class="vs-csat-title">
                              Sorry about that. What could we do better?
                            </div>
                            <textarea
                              class="vs-csat-comment"
                              rows={2}
                              maxLength={500}
                              value={csatComment}
                              placeholder="Optional"
                              aria-label="Your comment"
                              onInput={(e) => setCsatComment(e.currentTarget.value)}
                            />
                            <div class="vs-csat-actions">
                              <button
                                type="button"
                                class="vs-prechat-submit"
                                onClick={() => void sendRatingComment(false)}
                              >
                                Send
                              </button>
                              <button
                                type="button"
                                class="vs-csat-skip"
                                onClick={() => void sendRatingComment(true)}
                              >
                                Skip
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div class="vs-bubble">Thanks for your feedback!</div>
                        )}
                      </div>
                    )}
                    {csat === 'hidden' &&
                      !oldChat &&
                      messages.some((m) => m.senderType === 'ai' || m.senderType === 'agent') &&
                      !wasRated(widgetKey, messages) && (
                        <button type="button" class="vs-rate-chat" onClick={() => askRating()}>
                          Rate this chat
                        </button>
                      )}
                  </div>
                  {showSuggestions && (
                    <div class="vs-suggestions">
                      {suggestions.map((q) => (
                        <button
                          key={q}
                          type="button"
                          class="vs-chip"
                          disabled={!token || sending}
                          onClick={() => void send(q)}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                  {notice && (
                    <div class="vs-notice" role="alert">
                      {notice}
                    </div>
                  )}
                  {oldChat ? (
                    <div class="vs-composer vs-ended">
                      <div class="vs-ended-text">This chat has ended.</div>
                      <div class="vs-ended-actions">
                        <button type="button" class="vs-prechat-submit" onClick={newChat}>
                          Start a new chat
                        </button>
                        {messages.length > 0 && (
                          <button
                            type="button"
                            class="vs-ask-link"
                            onClick={() => setOldChat(null)}
                          >
                            Back to current chat
                          </button>
                        )}
                      </div>
                      <div class="vs-brand">
                        Powered by <strong>VixSync</strong>
                      </div>
                    </div>
                  ) : askStep ? (
                    <form
                      class="vs-composer vs-ask"
                      onSubmit={(e) => {
                        e.preventDefault();
                        void answerAsk(false);
                      }}
                      noValidate
                    >
                      <div class={`vs-input-wrap${askErr ? ' has-error' : ''}`}>
                        <label class="vs-sr" for="vs-ask-input">
                          {ASK_INPUT[askStep].placeholder}
                        </label>
                        <input
                          id="vs-ask-input"
                          ref={askInputRef}
                          type={ASK_INPUT[askStep].type}
                          inputMode={ASK_INPUT[askStep].inputMode}
                          autocomplete={ASK_INPUT[askStep].autocomplete}
                          placeholder={ASK_INPUT[askStep].placeholder}
                          maxLength={askStep === 'email' ? 254 : 100}
                          value={askValue}
                          aria-invalid={askErr ? true : undefined}
                          aria-describedby={askErr ? 'vs-ask-err' : undefined}
                          onInput={(e) => {
                            setAskValue((e.target as HTMLInputElement).value);
                            setAskErr(null);
                          }}
                        />
                        {(askMode === 'call'
                          ? askStep === 'email'
                          : config?.widget.askDetails?.[askStep] !== 'required') && (
                          <button
                            type="button"
                            class="vs-skip-icon"
                            aria-label="Skip this question"
                            title="Skip this question"
                            disabled={askBusy}
                            onClick={() => void answerAsk(true)}
                          >
                            <Svg size={17}>{Icon.skip}</Svg>
                          </button>
                        )}
                        <button
                          type="submit"
                          class={`vs-send${askValue.trim() ? ' is-ready' : ''}`}
                          disabled={askBusy || !askValue.trim()}
                          aria-label="Send"
                        >
                          <Svg size={18}>{Icon.send}</Svg>
                        </button>
                      </div>
                      {askErr && (
                        <div id="vs-ask-err" class="vs-ask-error" role="alert">
                          {askErr}
                        </div>
                      )}
                      <div class="vs-ask-actions">
                        {(askMode === 'call'
                          ? askStep === 'email'
                          : config?.widget.askDetails?.[askStep] !== 'required') && (
                          <button
                            type="button"
                            class="vs-ask-link"
                            onClick={() => void answerAsk(true)}
                          >
                            <Svg size={12}>{Icon.skip}</Svg>
                            Skip
                          </button>
                        )}
                        <button type="button" class="vs-ask-link" onClick={dismissAsk}>
                          Not now
                        </button>
                        {askMode === 'call' && config?.widget.call?.phoneNumber && (
                          <a
                            class="vs-ask-link vs-callnow"
                            href={`tel:${config.widget.call.phoneNumber}`}
                          >
                            <Svg size={12}>{Icon.phone}</Svg>
                            Call us now instead
                          </a>
                        )}
                      </div>
                    </form>
                  ) : (
                    <form
                      class="vs-composer"
                      onSubmit={(e) => {
                        e.preventDefault();
                        void send(draft);
                      }}
                    >
                      {noting ? (
                        <NoteBar
                          onSend={(note, url) => void sendNote(note, url)}
                          onClose={(error) => {
                            setNoting(false);
                            if (error) setNotice(error);
                          }}
                        />
                      ) : (
                        <div class="vs-input-wrap">
                          <label class="vs-sr" for="vs-input">
                            Message
                          </label>
                          <textarea
                            id="vs-input"
                            ref={inputRef}
                            rows={1}
                            maxLength={2000}
                            placeholder={token ? 'Type your message…' : 'Connecting…'}
                            value={draft}
                            disabled={!token}
                            onInput={(e) => {
                              const el = e.target as HTMLTextAreaElement;
                              onDraft(el.value, el);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                void send(draft);
                              }
                            }}
                          />
                          {canNote && !draft.trim() ? (
                            <button
                              type="button"
                              class="vs-send vs-mic"
                              disabled={!token || sending}
                              aria-label="Record a voice note"
                              title="Record a voice note"
                              onClick={() => {
                                setNotice(null);
                                setNoting(true);
                              }}
                            >
                              <Svg size={18}>{Icon.mic}</Svg>
                            </button>
                          ) : (
                            <button
                              type="submit"
                              class={`vs-send${draft.trim() ? ' is-ready' : ''}`}
                              disabled={!token || sending || !draft.trim()}
                              aria-label="Send"
                            >
                              <Svg size={18}>{Icon.send}</Svg>
                            </button>
                          )}
                        </div>
                      )}
                      <div class="vs-brand">
                        Powered by <strong>VixSync</strong>
                      </div>
                    </form>
                  )}
                </>
              )}
            </div>
          )}
          {detailsFor && view === 'chat' && !formInCall && (
            <DetailsSheet
              key={detailsFor.id}
              block={detailsFor.block}
              onSubmit={submitDetails}
              onClose={() => setDetailsFor(null)}
              optInLabel={whatsAppOptInLabel}
              callOptInLabel={callOptInLabel}
            />
          )}
        </section>
      )}
      <div class="vs-launch-row">
        {teaser && !open && greeting && (
          <div class="vs-teaser" role="status">
            <button
              type="button"
              class="vs-teaser-close"
              aria-label="Dismiss"
              onClick={() => {
                setTeaser(false);
                dismissTeaser(widgetKey);
              }}
            >
              <Svg size={12}>{Icon.close}</Svg>
            </button>
            <button type="button" class="vs-teaser-body" onClick={toggle}>
              <BotAvatar src={avatar} bg={logoBg} size="sm" />
              <span>{greeting}</span>
            </button>
          </div>
        )}
        <button
          type="button"
          class="vs-launcher"
          aria-label={open ? 'Close chat' : 'Open chat'}
          aria-expanded={open}
          onClick={toggle}
        >
          <span class="vs-launcher-icon vs-icon-chat">
            <Svg size={26}>{Icon.chat}</Svg>
          </span>
          <span class="vs-launcher-icon vs-icon-close">
            <Svg size={26}>{Icon.down}</Svg>
          </span>
          {unread > 0 && !open && (
            <span class="vs-badge" aria-label={`${unread} new messages`}>
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
